import { cloneDeep } from 'lodash'

import { GET_CHAT_COMPLETIONS, GENERATE_SPEECH } from 'api/projectGenerator'
import { GET_ORGANIZATION_LIST } from 'api/users'
import { UPLOAD_AUDIO } from 'api/media'

import { LOCALES_DETAILS } from 'constants/locales'
import { PROJECT_MODES } from 'constants/projects'

import { getUint8Array } from 'utils/file'
import { encodeOrDecodeJsonStructure, getUniqueId } from 'utils/common'

import API from 'api'

import { PUBLISH_DESTINATION } from '../steps/SetupProjects/constants'
import { BLOCKS, FLIP_CARDS_BG_COLORS } from '../constants'

import { onGenerateImage, getImageUrl } from './'

const onGenerateStory = async ({ model }, scenarioSettings, topic) => {
    const { learningLocaleCode, theme, level } = scenarioSettings.data.common
    const { storyLength } = scenarioSettings.data.quiz
    const prompt = `Design a short story for ${LOCALES_DETAILS[learningLocaleCode].engLabel} language learners without any introduction and titles, just plain story. Topic: ${topic}. Theme: ${theme}. Student level: ${level}. Length is ${storyLength} words approximately.`
    return await GET_CHAT_COMPLETIONS(prompt, { model })
}

const onGenerateQuiz = async ({ model }, scenarioSettings, story) => {
    const { questionsCount } = scenarioSettings.data.quiz
    const prompt = `Story: ${story}'. Create ${questionsCount} simple questions with 4 possible answers for each question (1 correct, 3 not correct) about this text.
                Return JSON array of objects. Example: [{ text: \"What color are apples?\", answers: [\"Blue\", \"Red or green\", \"Purple\", \"Yellow\"], correctAnswerIndex: 1 }, { text: \"What is Sue learning about?\", answers: [\"Animals\", \"Clothes\", \"Cars\", \"Fruits and vegetables\"], correctAnswerIndex: 0 }]
            `
    const response = await GET_CHAT_COMPLETIONS(prompt, { model })
    const questions = JSON.parse(response).map(question => ({
        id: getUniqueId(),
        text: question.text,
        answers: question.answers.map((answer, answerIndex) => ({
            id: getUniqueId(),
            text: answer,
            isCorrect: answerIndex === question.correctAnswerIndex,
        })),
    }))
    return {
        questions,
        results: [
            {
                id: getUniqueId(),
                imageUrl: null,
                title: 'Try it again!',
                description: '',
                imageDisclaimer: '',
            },
            {
                id: getUniqueId(),
                imageUrl: null,
                title: 'Congratulations!',
                description: '',
                imageDisclaimer: '',
            },
        ],
    }
}

const onGenerateVocabulary = async ({ model }, scenarioSettings, story) => {
    const { level, nativeLocaleCode } = scenarioSettings.data.common
    const { cardsCount } = scenarioSettings.data.vocabulary
    const prompt = `Story: '${story}'. Create a vocabulary: a list of ${cardsCount} words which can be most complicated for a ${level}-level student. Next to each word put a translate of word on student native ${LOCALES_DETAILS[nativeLocaleCode].engLabel} language.
    Return JSON array of objects. Example for russian native speaker who learns english:
        [{ learning: \"Soccer\", native: \"Футбол\" }, { learning: \"Friends\", native: \"Друзья\" }, { learning: \"Player\", native: \"Игрок\" }]
    `
    const response = await GET_CHAT_COMPLETIONS(prompt, { model })
    return JSON.parse(response).map(data => ({ id: getUniqueId(), ...data }))
}

const onGetManyTopics = async ({ model, numberOfProjects }, scenarioSettings) => {
    const { learningLocaleCode, theme, level } = scenarioSettings.common
    const prompt = `Design a new topics idea which can be used to prepare for ${LOCALES_DETAILS[learningLocaleCode].engLabel} classes. Topic language: ${LOCALES_DETAILS[learningLocaleCode].engLabel}. Theme: ${theme}. Student level: ${level}. Generate only ${numberOfProjects} topic(s). Return JSON array of strings. Response example: [\"The day with my pet\", \"My weekend with family\", \"My friends\", \"In the park\", \"The city of London\"]"`

    const response = await GET_CHAT_COMPLETIONS(prompt, { model })
    return JSON.parse(response)
}

const onGetOneTopic = async ({ model, numberOfProjects }, scenarioSettings, excludeTopics) => {
    const { learningLocaleCode, theme, level } = scenarioSettings.common
    let prompt
    if (excludeTopics?.length) {
        prompt = `Design a new topic idea which can be used to prepare for ${
            LOCALES_DETAILS[learningLocaleCode].engLabel
        } classes. Topic language: ${
            LOCALES_DETAILS[learningLocaleCode].engLabel
        }. Theme: ${theme}. Student level: ${level}. Generate only one topic. Return string. Response example: The day with my pet. Exclude topics: ${excludeTopics
            .map(topic => `'${excludeTopics}'`)
            .join(', ')}`
    } else {
        prompt = `Design a new topic idea which can be used to prepare for ${LOCALES_DETAILS[learningLocaleCode].engLabel} classes. Topic language: ${LOCALES_DETAILS[learningLocaleCode].engLabel}. Theme: ${theme}. Student level: ${level}. Generate only one topic. Return string. Response example: The day with my pet.`
    }

    const response = await GET_CHAT_COMPLETIONS(prompt, { model })
    return response.replace(/['‘’"“”]/g, '')
}

const onGenerateProjects = async (
    mainSettings,
    scenarioSettings,
    { onProgress = () => {}, onProcessed = () => {} } = {},
) => {
    const projects = []
    for (const topic of scenarioSettings.topics) {
        try {
            onProgress(topic)
            const project = {
                topic,
                voice: scenarioSettings.data.quiz.voice,
                speech: '',
            }
            const [imageUrl, story] = await Promise.all([
                onGenerateImage(topic),
                onGenerateStory(mainSettings, scenarioSettings, topic),
            ])
            project.imageUrl = imageUrl
            project.story = story
            if (mainSettings.blocks.includes(BLOCKS.FLIP_CARDS)) {
                const [quiz, vocabulary] = await Promise.all([
                    onGenerateQuiz(mainSettings, scenarioSettings, story),
                    onGenerateVocabulary(mainSettings, scenarioSettings, story),
                ])
                project.quiz = quiz
                project.vocabulary = vocabulary
            } else {
                project.quiz = await onGenerateQuiz(mainSettings, scenarioSettings, story)
            }

            projects.push(project)

            onProcessed(true)
        } catch (err) {
            console.error(err)
            onProcessed(false)
        }
    }

    return projects
}

const onGenerateProjectJSON = async ({ project, mainSettings }) => {
    const _project = cloneDeep(project)

    _project.main.speech = await GENERATE_SPEECH(_project.main.story, { voiceId: _project.main.voice })

    _project.main.imageUrl = await getImageUrl(_project.main.imageUrl)

    const [organizations, uint8Array] = await Promise.all([
        GET_ORGANIZATION_LIST(),
        getUint8Array(_project.main.speech),
    ])

    const audioData = await UPLOAD_AUDIO({
        fileName: 'generated.mp3',
        bytes: uint8Array,
        contentType: 'audio/mpeg',
        selectedOrganizationId: organizations[0].id,
    })

    const { topic, imageUrl, quiz, vocabulary } = _project.main

    const app = {
        bg: '#fff',
        maxWidth: 800,
        colorTheme: '#2990fb',
        customStyles: '',
        rememberEmail: false,
        backgroundImage: '',
        customTranslations: '',
        isTransparentBackground: false,
        multiplayerBackgroundImage: 'https://p.interacty.me/multiplayer/realtime-games-background-image.png',
    }

    let blocks = []

    mainSettings.blocks.forEach(block => {
        if (block === BLOCKS.TEXT) {
            const textBlock = {
                t: 1,
                id: 'chqxve',
                text: `U+60;p class=U+34;ql-align-centerU+34;U+62;U+60;strong class=U+34;ql-font-MontserratU+34; style=U+34;line-height: 1.2; font-size: 48px; color: rgb(0, 0, 0);U+34;U+62;${topic}U+60;/strongU+62;U+60;/pU+62;U+60;p class=U+34;ql-align-centerU+34;U+62;U+60;brU+62;U+60;/pU+62;U+60;p class=U+34;ql-align-centerU+34;U+62;U+60;span class=U+34;ql-font-UbuntuU+34; style=U+34;color: rgb(68, 68, 68); font-size: 18px; line-height: 1.5;U+34;U+62;Listen and choose the correct answerU+60;/spanU+62;U+60;span style=U+34;font-size: 16px; line-height: 1.5;U+34;U+62;:U+60;/spanU+62;U+60;/pU+62;`,
                wP_bg: '#fff',
            }

            blocks = [...blocks, textBlock]
        }
        if (block === BLOCKS.IMAGE) {
            const imageBlock = {
                t: 2,
                id: getUniqueId(),
                url: imageUrl,
                blur: false,
                width: 100,
                position: {
                    label: 'Center',
                    value: 'center',
                },
                imageSize: {
                    label: 'Full width',
                    value: 'fullWidth',
                },
            }

            blocks = [...blocks, imageBlock]
        }
        if (block === BLOCKS.TRIVIA_QUIZ) {
            const audioBlock = {
                t: 23,
                id: getUniqueId(),
                url: audioData.url,
                name: `${topic}_[auto-generated].mp3`,
                isLoop: false,
                isMuted: false,
                isAutoPlay: false,
                progressJumpSteps: {
                    forward: 10,
                    backward: 10,
                },
                firstLineBarControls: [
                    'PLAY',
                    'REWIND',
                    'CURRENT_TIME',
                    'PROGRESS_BAR',
                    'DURATION',
                    'VOLUME_BAR',
                    'SPEED',
                ],
                secondLineBarControls: [],
            }

            const quizThreshold = quiz.length / 2
            const firstResultTo = Math.floor(quizThreshold)

            const triviaQuizBlock = {
                t: 9,
                id: getUniqueId(),
                struct: {
                    cover: {
                        image: 'https://media.interacty.me/default/default-image-bg-big.svg',
                        header: 'My Quiz',
                        buttonText: 'Start',
                        description: 'Description of the quiz',
                        isShowCover: false,
                        imageDisclaimer: '',
                    },
                    questions: quiz.questions.map(question => ({
                        id: question.id,
                        text: question.text,
                        image: null,
                        isText: true,
                        answers: question.answers.map(answer => ({
                            id: answer.id,
                            text: answer.text,
                            image: null,
                            isCorrect: answer.isCorrect,
                            imageLabel: '',
                            description: '',
                            imageDescription: '',
                        })),
                        imageDisclaimer: '',
                    })),
                    results: quiz.results.map(result => ({
                        id: result.id,
                        image: result.imageUrl,
                        header: result.title,
                        description: result.description,
                        imageDisclaimer: '',
                    })),
                    resultDistribution: [
                        {
                            to: firstResultTo,
                            from: 0,
                            isVisible: true,
                        },
                        {
                            to: quiz.length,
                            from: firstResultTo + 1,
                            isVisible: true,
                        },
                    ],
                },
                colorTheme: '#2990fb',
                showScores: true,
                progressBar: true,
                isShowLeadForm: false,
                leadFormStruct: {
                    form: {
                        fields: [
                            {
                                key: 'firstName',
                                type: 'TEXT_INPUT',
                                label: 'First name',
                                isRequired: true,
                                initialValue: '',
                            },
                            {
                                key: 'email',
                                type: 'EMAIL',
                                label: 'Email',
                                isRequired: true,
                                initialValue: '',
                            },
                        ],
                        logoImage: '',
                        buttonText: 'Submit',
                        headerText: 'Form',
                        descriptionText: 'Please fill in the form fields',
                        linkPrivacyPolicyLink: '',
                    },
                    isHasFinalScreen: false,
                },
                redirectTimeout: 4,
                callToActionLink: '',
                callToActionText: '',
                isRedirectEnabled: false,
                redirectTargetLink: '',
                callToActionEnabled: false,
            }

            blocks = [...blocks, audioBlock, triviaQuizBlock]
        }
        if (block === BLOCKS.FLIP_CARDS) {
            const flipCardsBlock = vocabulary.map((card, index) => {
                let colorIndex
                const maxIndex = FLIP_CARDS_BG_COLORS.length - 1
                if (index <= maxIndex) {
                    colorIndex = index
                } else {
                    colorIndex = (index % maxIndex) - 1
                }

                return {
                    t: 4,
                    id: getUniqueId(),
                    frontSrc: null,
                    frontText: `U+60;p class=U+34;ql-align-centerU+34;U+62;U+60;strong class=U+34;ql-font-UbuntuU+34;em style=U+34;font-size: 48px; color: rgb(255, 255, 255);U+34;U+62;${card.learning} U+60;/emU+62;U+60;/pU+62;`,
                    frontColor: FLIP_CARDS_BG_COLORS[colorIndex][0],
                    backSrc: null,
                    backText: `U+60;p class=U+34;ql-align-centerU+34;U+62;U+60;strong class=U+34;ql-font-UbuntuU+34;em style=U+34;font-size: 36px; color: rgb(255, 255, 255);U+34;U+62;${card.native} U+60;/emU+62;U+60;/pU+62;`,
                    backColor: FLIP_CARDS_BG_COLORS[colorIndex][1],
                    imageProportions: {
                        label: '16:9',
                        value: '16|9',
                    },
                }
            })

            blocks = [...blocks, ...flipCardsBlock]
        }
    })

    return {
        app,
        pages: [
            {
                id: getUniqueId(),
                name: 'Home',
                blocks,
            },
        ],
        integrations: {},
    }
}

const onPublishProject = async (projects, mainSettings) => {
    for (const project of projects) {
        try {
            if (project.system.publishSettings[PUBLISH_DESTINATION.INTERACTY].isActive) {
                const structureJson = await onGenerateProjectJSON({ project, mainSettings })

                let templateImageUrl = await getImageUrl(
                    project.system.publishSettings[PUBLISH_DESTINATION.INTERACTY].templateImageUrl,
                )
                if (!templateImageUrl) templateImageUrl = await getImageUrl(project.main.imageUrl)

                const data = {
                    projectStructureJson: JSON.stringify(encodeOrDecodeJsonStructure(structureJson, 'encode')),
                    isPremium: false,
                    imageUrl: templateImageUrl,
                    orderNumber: 2020,
                    enabled: false,
                    projectType: { id: process.env.REACT_APP_PROJECT_TYPE_ID },
                    projectPdfUrl: null,
                    name: [
                        {
                            text:
                                project.system.publishSettings[PUBLISH_DESTINATION.INTERACTY].templateName ||
                                project.main.topic,
                            languageCode: 'en',
                        },
                    ],
                    isShowDescription: false,
                    description: [],
                    mode: PROJECT_MODES.SINGLE,
                    languageTemplateTag: mainSettings.languageTemplateTag,
                    roleTemplateTag: mainSettings.roleTemplateTag,
                    tags: mainSettings.tags,
                }

                if (project.system.publishSettings[PUBLISH_DESTINATION.INTERACTY].templateDescription) {
                    data.description = [
                        {
                            text: project.system.publishSettings[PUBLISH_DESTINATION.INTERACTY].templateDescription,
                            languageCode: 'en',
                        },
                    ]
                }

                await API.TEMPLATES.CREATE_TEMPLATE(data)
            }
        } catch (err) {
            console.error(err)
        }
    }
}

export default {
    onGetManyTopics,
    onGetOneTopic,
    onGenerateProjects,
    onGenerateProjectJSON,
    onPublishProject,
}
